// ---------------------------------------------------------------------------
// ExportButton -- Leaf Component
// ---------------------------------------------------------------------------
// Button that exports table data as a CSV file.
// Includes a pure `generateCsv` helper for testability.
// ---------------------------------------------------------------------------

import React, { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExportButtonProps {
  readonly data: readonly Record<string, unknown>[]
  readonly columns: readonly { key: string; label: string }[]
  readonly filename?: string
  readonly disabled?: boolean
}

// ---------------------------------------------------------------------------
// Pure helper: CSV generation
// ---------------------------------------------------------------------------

function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }

  const str = String(value)

  // If value contains commas, quotes, or newlines, wrap in quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }

  return str
}

function generateCsv(
  data: readonly Record<string, unknown>[],
  columns: readonly { key: string; label: string }[]
): string {
  const headerRow = columns.map((col) => escapeCsvValue(col.label)).join(',')

  if (data.length === 0) {
    return headerRow
  }

  const dataRows = data.map((row) =>
    columns.map((col) => escapeCsvValue(row[col.key])).join(',')
  )

  return [headerRow, ...dataRows].join('\n')
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ExportButton({
  data,
  columns,
  filename = 'export',
  disabled = false,
}: ExportButtonProps) {
  const isDisabled = disabled || data.length === 0

  const handleExport = useCallback(() => {
    if (isDisabled) return

    const csv = generateCsv(data, columns)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.csv`
    link.click()

    URL.revokeObjectURL(url)
  }, [data, columns, filename, isDisabled])

  return (
    <Button
      data-testid="export-button"
      type="button"
      variant="outline"
      size="sm"
      disabled={isDisabled}
      onClick={handleExport}
    >
      <Download className="h-4 w-4" />
      Export
    </Button>
  )
}

export { ExportButton, generateCsv }
export type { ExportButtonProps }
